import { Icon } from '@iconify/react';
import {
  Box,
  DialogContent,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { generateClient } from 'aws-amplify/api';
import React, { useEffect, useState } from 'react';
import { Schema } from '../../../../../../amplify/data/resource';

// @ts-ignore
type Template = Schema['WhatsAppTemplate']['type'];

interface TemplatesListProps {
  setTemplateId: (id: string) => void;
}

const apiClient = generateClient<Schema>();

const TemplatesList: React.FC<TemplatesListProps> = ({ setTemplateId }) => {
  const theme = useTheme();
  const [templates, setTemplates] = useState<Template[]>([]);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const subscription =
          apiClient.models.WhatsAppTemplate.observeQuery().subscribe({
            next: ({ items }: { items: any[] }) => {
              setTemplates(items);
            },
            error: (error: any) => {
              console.error('Error fetching clients:', error);
            },
          });

        return () => subscription.unsubscribe();
      } catch (error) {
        console.error('Error during fetchUsers:', error);
      }
    };

    fetchTemplates();
  }, []);

  return (
    <DialogContent
      sx={{
        display: 'flex',
        flexDirection: 'column',
        pt: '70px',
        px: '100px',
        backgroundColor: 'transparent',
        flex: 1,
        overflowY: 'auto',
        boxSizing: 'border-box',
        position: 'relative',
        zIndex: 1,
      }}
    >
      <Stack>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" alignItems="center" spacing="10px">
            <img
              alt="logo"
              src={'/assets/client/add.svg'}
              onClick={() => {
                setTemplateId('create');
              }}
              style={{ cursor: 'pointer', zIndex: 200 }}
            />
            <Typography
              fontSize="32px"
              fontWeight="300"
              fontFamily={'AlmoniDL'}
            >
              הוסף הודעה חדשה
            </Typography>
          </Stack>
          <Typography fontSize="60px" fontWeight="300" fontFamily={'AlmoniDL'}>
            הוסף הודעה חדשה
          </Typography>
        </Stack>
        <Box sx={{ direction: 'rtl' }}>
          <TableContainer
            component={Paper}
            sx={{
              backgroundColor: 'transparent',
              boxShadow: 'none',
              overflowY: 'auto',
            }}
          >
            <Table
              stickyHeader
              sx={{
                backgroundColor: 'transparent',
                borderCollapse: 'separate',
                borderSpacing: '0 10px',
              }}
            >
              <TableHead>
                <TableRow sx={{ backgroundColor: 'transparent' }}>
                  <TableCell sx={tableHeaderCellStyle}>מספר</TableCell>
                  <TableCell sx={tableHeaderCellStyle}>שם</TableCell>
                  <TableCell sx={tableHeaderCellStyle}>תוכן</TableCell>
                  <TableCell sx={tableHeaderCellStyle}>עריכה</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {templates.map((template, index) => (
                  <TemplateItem
                    key={template.id}
                    id={template.id}
                    index={index + 1}
                    title={template.title || '-'}
                    text={template.text || '-'}
                    setTemplateId={setTemplateId}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Stack>
    </DialogContent>
  );
};

export default TemplatesList;

const tableHeaderCellStyle = {
  padding: '10px 20px',
  borderBottom: 'none',
  textAlign: 'right',
  backgroundColor: 'transparent',
  fontSize: '14px',
  fontWeight: 700,
  fontFamily: 'AlmoniDL',
};

interface TemplateItemProps {
  id: string;
  setTemplateId: (id: string) => void;
  index: number;
  title: string;
  text: string;
}

const TemplateItem: React.FC<TemplateItemProps> = ({
  index,
  id,
  title,
  text,
  setTemplateId,
}) => {
  return (
    <TableRow
      sx={{
        backgroundColor: '#F2F4F7',
        borderRadius: '10px',
        p: '20px',
        overflow: 'hidden',
        '&:last-child td, &:last-child th': { border: 0 },
        boxShadow: 'none',
        textAlign: 'right',
        cursor: 'pointer',
      }}
    >
      <TableCell
        sx={{
          backgroundColor: '#F2F4F7',
          borderTopRightRadius: '10px',
          borderBottomRightRadius: '10px',
          p: '20px',
          overflow: 'hidden',
          '&:last-child td, &:last-child th': { border: 0 },
          boxShadow: 'none',
          textAlign: 'right',
          cursor: 'pointer',
        }}
      >
        <Typography
          dir="rtl"
          fontSize="14px"
          fontWeight="700"
          fontFamily="AlmoniDL"
        >
          {index}
        </Typography>
      </TableCell>

      {/* Имя */}
      <TableCell
        sx={{
          backgroundColor: '#F2F4F7',

          p: '20px',
          overflow: 'hidden',
          '&:last-child td, &:last-child th': { border: 0 },
          boxShadow: 'none',
          textAlign: 'right',
          cursor: 'pointer',
        }}
      >
        <Typography
          dir="rtl"
          fontSize="14px"
          fontWeight="700"
          fontFamily="AlmoniDL"
        >
          {title}
        </Typography>
      </TableCell>

      {/* Содержимое */}
      <TableCell
        sx={{
          backgroundColor: '#F2F4F7',

          p: '20px',
          overflow: 'hidden',
          '&:last-child td, &:last-child th': { border: 0 },
          boxShadow: 'none',
          textAlign: 'right',
          cursor: 'pointer',
        }}
      >
        <Typography
          dir="rtl"
          fontSize="14px"
          fontWeight="700"
          fontFamily="AlmoniDL"
        >
          {text}
        </Typography>
      </TableCell>

      {/* Кнопка редактирования */}
      <TableCell
        sx={{
          backgroundColor: '#F2F4F7',
          borderTopLeftRadius: '10px',
          borderBottomLeftRadius: '10px',
          p: '20px',
          overflow: 'hidden',
          '&:last-child td, &:last-child th': { border: 0 },
          boxShadow: 'none',
          textAlign: 'right',
          cursor: 'pointer',
        }}
      >
        <IconButton onClick={() => setTemplateId(id)} color="primary">
          <Icon icon="mdi:pencil-outline" color="black" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};
